import * as React from "react";
import { useState, useEffect, useContext, useMemo } from "react";
import Select from "react-select";

// react-bootstrap components
import {
  Button,
  Card,
  Container,
  Row,
  Col,
  Spinner,
  Form,
} from "react-bootstrap";

import { NotificationAlertContext } from "contexts/notificationAlertContext";
import { AuthContext } from "contexts/authContext";
import {
  tourGuideSteps,
  scheduleReportModalSteps,
} from "views/Reports/ManifestOutcomeContentReportSteps";
import { useTour } from "@reactour/tour";
import CustomReactDatetime from "components/Custom/CustomReactDatetime.js";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { getList, addScheduledReport } from "graphql/queries.js";
import { API, graphqlOperation } from "aws-amplify";
import _ from "lodash";
import moment from "moment";
import ReactDatetime from "react-datetime";
import CustomTooltip from "components/Custom/CustomTooltip";
import {
  MultiValueWithCustomTooltip,
  customEllipsisMultiSelectStyles,
} from "components/Custom/CustomMultiSelect.js";
import {
  checkEvenNumberQuotes,
  cleanStringToArray,
} from "utilities/commonUtilities";

let channel_client_hashmap = {};
let client_channel_hashmap = {};
let channel_environment_hashmap = {};
let environment_channel_hashmap = {};
let environment_client_hashmap = {};
let client_environment_hashmap = {};

const all_option = { value: "ALL", label: "         All" };
const none_option = { value: "NONE", label: "         None" };

const default_message_status_options = [
  { value: "SENT", label: "Sent" },
  { value: "FILTERED", label: "Filtered" },
  { value: "ERROR", label: "Error" },
];

let default_client_options = [];
let default_environment_options = [];
let default_channel_options = [];

function ManifestOutcomeContentReport() {
  // Contexts
  const notify = useContext(NotificationAlertContext);
  const currentUser = useContext(AuthContext);
  const userClientId = currentUser.clientId;

  // States
  const [selectedStartDate, setSelectedStartDate] = useState(
    moment().startOf("day")
  );
  const [selectedEndDate, setSelectedEndDate] = useState(moment().endOf("day"));
  const [selectedChannelIds, setSelectedChannelIds] = useState([]);
  const [selectedClientEnvironmentIds, setSelectedClientEnvironmentIds] =
    useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [selectedMessageStatus, setSelectedMessageStatus] = useState([]);

  const [clientOptions, setClientOptions] = useState([]);
  const [clientEnvironmentOptions, setClientEnvironmentOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [showLoader, setShowLoader] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  const [changedOption, setChangedOption] = useState(null);
  const [showScheduleReportModal, setShowScheduleReportModal] = useState(false);
  const [selectedReportName, setSelectedReportName] = useState(
    "My Manifest Outcome and Content Report"
  );
  const [selectedRunReportDate, setSelectedRunReportDate] = useState(moment());
  const [selectedReportDistribution, setSelectedReportDistribution] =
    useState(false);
  const [validated, setValidated] = useState(false);

  const [searchByCustomMetadata, setSearchByCustomMetadata] = useState(false);

  // Tour Guide
  const { setIsOpen, setCurrentStep, setSteps } = useTour();

  const loadHashes = async () => {
    fetchList("CHANNEL_CLIENT_HASH");
    fetchList("CLIENT_CHANNEL_HASH");
    fetchList("CHANNEL_ENVIRONMENT_HASH");
    fetchList("ENVIRONMENT_CHANNEL_HASH");
    fetchList("ENVIRONMENT_CLIENT_HASH");
    fetchList("CLIENT_ENVIRONMENT_HASH");
  };

  const fetchList = async (type) => {
    try {
      setShowLoader(true);
      const response = await API.graphql(
        graphqlOperation(getList, {
          type: type,
          auth_client_id: userClientId,
        })
      );
      if (response.data?.getList?.success) {
        let options = response.data.getList.body
          ? response.data.getList.body
              .map((data) => {
                const option = {
                  value: data.id,
                  label: data.name,
                  env: data.env,
                };
                if (type == "CHANNEL_CLIENT_HASH") {
                  channel_client_hashmap[data.id] = data.hash;
                  default_channel_options.push(option);
                } else if (type == "CLIENT_CHANNEL_HASH") {
                  client_channel_hashmap[data.id] = data.hash;
                  default_client_options.push(option);
                } else if (type == "CHANNEL_ENVIRONMENT_HASH") {
                  channel_environment_hashmap[data.id] = data.hash;
                } else if (type == "ENVIRONMENT_CHANNEL_HASH") {
                  environment_channel_hashmap[data.id] = data.hash;
                  default_environment_options.push(option);
                } else if (type == "ENVIRONMENT_CLIENT_HASH") {
                  environment_client_hashmap[data.id] = data.hash;
                } else if (type == "CLIENT_ENVIRONMENT_HASH") {
                  client_environment_hashmap[data.id] = data.hash;
                }
                return option;
              })
              .sort((a, b) => a.label.localeCompare(b.label))
          : [];

        if (type == "CLIENT_CHANNEL_HASH") {
          setClientOptions(options);
        } else if (type == "CHANNEL_CLIENT_HASH") {
          setChannelOptions(options);
        } else if (type == "ENVIRONMENT_CHANNEL_HASH") {
          setClientEnvironmentOptions(options);
        }
      } else {
        console.error(response.data?.getList?.message);
        notify("danger", "Could not fetch list - " + type);
      }
    } catch (error) {
      console.error(error);
      notify("danger", "Could not fetch list - " + type);
    } finally {
      setShowLoader(false);
    }
  };

  function findObjectByProperty(arr, key, value) {
    return arr.find((obj) => obj.hasOwnProperty(key) && obj[key] === value);
  }

  const addReport = async (parameters) => {
    notify("info", "Requesting report to be scheduled");
    try {
      let request_env_list = [];
      selectedChannelIds.forEach(function (channel) {
        if (!request_env_list.includes(channel.env)) {
          request_env_list.push(channel.env);
        }
      });

      const response = await API.graphql(
        graphqlOperation(addScheduledReport, {
          report_name: selectedReportName,
          report_distribution: selectedReportDistribution ? 1 : 0,
          created_by_user_org: userClientId,
          report_run_date: selectedRunReportDate.format(),
          report_parameters: JSON.stringify(parameters),
          report_type: "ManifestOutcomeContentReport",
          requested_environments: request_env_list,
        })
      );
      if (response.data?.addScheduledReport?.success) {
        notify("success", "");
      } else {
        console.error(response.data?.addScheduledReport?.message);
        notify("danger", "Failed to schedule the report");
      }
    } catch (error) {
      console.error(error);
      notify("danger", "Failed to schedule the report");
    }
  };

  useEffect(() => {
    async function fetchData() {
      loadHashes();
    }
    fetchData();
  }, []);

  useEffect(() => {
    rebaseOptionsAndSelected();
  }, [
    selectedClientIds,
    selectedClientEnvironmentIds,
    selectedChannelIds,
    clientOptions,
    clientEnvironmentOptions,
    channelOptions,
  ]);

  const [formValues, setFormValues] = React.useState([
    { customMetadataKey: "", customMetadataValueList: "" },
  ]);

  const handleChange = (i, e) => {
    let newFormValues = [...formValues];
    newFormValues[i][e.target.name] = e.target.value;
    setFormValues(newFormValues);
  };

  const addFormFields = () => {
    setFormValues([
      ...formValues,
      { customMetadataKey: "", customMetadataValueList: "" },
    ]);
  };

  const sortArray = (myArray) => {
    return myArray.sort((a, b) => a.label.localeCompare(b.label));
  };
  const compareArrays = (a, b) => {
    return JSON.stringify(a) === JSON.stringify(b);
  };
  const uniqueArray = (myArray) => {
    if (!myArray || myArray.length === 0) return [];
    return myArray.filter((obj, index) => {
      if (!obj) return false;
      return index === myArray.findIndex((o) => obj.value === o.value);
    });
  };

  const isCustomMetadataValid = () => {
    for (let form of formValues) {
      if (!form.customMetadataKey) {
        return false;
      }
    }
    return true;
  };

  const isCustomMetadataValueValid = () => {
    for (let form of formValues) {
      if (!form.customMetadataValueList) {
        return false;
      } else {
        if (!checkEvenNumberQuotes(form.customMetadataValueList)) {
          return false;
        }
      }
    }
    return true;
  };

  const generateReportParameters = () => {
    let query_data_list = [];
    let custom_metadata_list = [];
    let message_status_list = [];

    selectedChannelIds.forEach(function (channel) {
      let found_obj = findObjectByProperty(
        query_data_list,
        "client_environment_id",
        channel.env
      );
      if (found_obj) {
        found_obj["channel_ids"].push(channel.value);
      } else {
        let new_obj = {
          client_environment_id: channel.env,
          channel_ids: [channel.value],
        };
        query_data_list.push(new_obj);
      }
    });

    custom_metadata_list = formValues.filter((form) => form.customMetadataValueList)
          .map((form) => {
            return {
              custom_metadata_key: form.customMetadataKey,
              custom_metadata_value_list: cleanStringToArray(form.customMetadataValueList)
            }
          });
    
    selectedMessageStatus.forEach(function (status) {
      message_status_list.push(status.value);
    });

    const parameters = {
      start_date: selectedStartDate.format(),
      end_date: selectedEndDate.format(),
      client_environment_channels: query_data_list,
      msg_statuses: message_status_list,
      custom_metadata: custom_metadata_list,
    };
    return parameters;
  };

  const removeFormFields = (i) => {
    let newFormValues = [...formValues];
    newFormValues.splice(i, 1);
    setFormValues(newFormValues);
    if (newFormValues.length == 0) {
      setFormValues([{ customMetadataKey: "", customMetadataValueList: "" }]);
      setSearchByCustomMetadata(false);
    }
  };

  const rebaseOptionsAndSelected = () => {
    // Rebase the client options
    let allowable_client_channel = [];
    let allowable_client_env = [];
    if (selectedChannelIds.length > 0) {
      selectedChannelIds.forEach(function (channel) {
        allowable_client_channel = allowable_client_channel.concat(
          channel_client_hashmap[channel.value]
        );
      });
    } else {
      allowable_client_channel = default_client_options;
    }
    if (selectedClientEnvironmentIds.length > 0) {
      selectedClientEnvironmentIds.forEach(function (env) {
        allowable_client_env = allowable_client_env.concat(
          environment_client_hashmap[env.value]
        );
      });
    } else {
      allowable_client_env = default_client_options;
    }
    allowable_client_channel = uniqueArray(allowable_client_channel);
    allowable_client_env = uniqueArray(allowable_client_env);
    let newClientOptions = sortArray(
      allowable_client_channel.filter((o1) =>
        allowable_client_env.some((o2) => o1.value === o2.value)
      )
    );
    if (!compareArrays(newClientOptions, sortArray(clientOptions))) {
      setClientOptions(newClientOptions);
    }

    // Rebase the environment options
    let allowable_env_channel = [];
    let allowable_env_client = [];
    if (selectedChannelIds.length > 0) {
      selectedChannelIds.forEach(function (channel) {
        allowable_env_channel = allowable_env_channel.concat(
          channel_environment_hashmap[channel.value]
        );
      });
    } else {
      allowable_env_channel = default_environment_options;
    }
    if (selectedClientIds.length > 0) {
      selectedClientIds.forEach(function (client) {
        allowable_env_client = allowable_env_client.concat(
          client_environment_hashmap[client.value]
        );
      });
    } else {
      allowable_env_client = default_environment_options;
    }
    allowable_env_channel = uniqueArray(allowable_env_channel);
    allowable_env_client = uniqueArray(allowable_env_client);
    let newEnvironmentOptions = sortArray(
      allowable_env_channel.filter((o1) =>
        allowable_env_client.some((o2) => o1.value === o2.value)
      )
    );
    if (
      !compareArrays(newEnvironmentOptions, sortArray(clientEnvironmentOptions))
    ) {
      setClientEnvironmentOptions(newEnvironmentOptions);
    }

    // Rebase the channel options
    let allowable_channel_client = [];
    let allowable_channel_env = [];
    if (selectedClientIds.length > 0) {
      selectedClientIds.forEach(function (client) {
        allowable_channel_client = allowable_channel_client.concat(
          client_channel_hashmap[client.value]
        );
      });
    } else {
      allowable_channel_client = default_channel_options;
    }
    if (selectedClientEnvironmentIds.length > 0) {
      selectedClientEnvironmentIds.forEach(function (env) {
        allowable_channel_env = allowable_channel_env.concat(
          environment_channel_hashmap[env.value]
        );
      });
    } else {
      allowable_channel_env = default_channel_options;
    }
    allowable_channel_client = uniqueArray(allowable_channel_client);
    allowable_channel_env = uniqueArray(allowable_channel_env);
    let newChannelOptions = sortArray(
      allowable_channel_client.filter((o1) =>
        allowable_channel_env.some((o2) => o1.value === o2.value)
      )
    );
    if (!compareArrays(newChannelOptions, sortArray(channelOptions))) {
      setChannelOptions(newChannelOptions);
    }

    // Rebase the selected environment
    if (changedOption == "client") {
      let newEnvironmentSelected = selectedClientEnvironmentIds.filter(
        function (cItem) {
          return clientEnvironmentOptions.find(function (aItem) {
            return cItem.value === aItem.value && cItem.label === aItem.label;
          });
        }
      );
      if (
        !compareArrays(
          sortArray(newEnvironmentSelected),
          sortArray(selectedClientEnvironmentIds)
        ) &&
        newEnvironmentSelected.length > 0
      ) {
        setSelectedClientEnvironmentIds(newEnvironmentSelected);
      }
    }

    // Rebase the selected channel
    if (changedOption == "client" || changedOption == "environment") {
      let newChannelSelected = selectedChannelIds.filter(function (cItem) {
        return channelOptions.find(function (aItem) {
          return cItem.value === aItem.value && cItem.label === aItem.label;
        });
      });
      if (
        !compareArrays(
          sortArray(newChannelSelected),
          sortArray(selectedChannelIds)
        ) &&
        newChannelSelected.length > 0
      ) {
        setSelectedChannelIds(newChannelSelected);
      }
    }
  };


  if (showLoader) return <Spinner animation="grow" />;

  if (errorMessage) return errorMessage;

  return (
    <>
      <Container fluid>
        <Row>
          <Col md="10">
            <h2>
              Manifest Outcome and Content Report
            </h2>
          </Col>
          {currentUser.groupName === "external" && (
            <Col md="2" className="my-auto text-right">
              <Button
                variant="secondary"
                className="btn-outline"
                onClick={() => {
                  setSteps(tourGuideSteps);
                  setCurrentStep(0);
                  setIsOpen(true);
                }}
              >
                <i className="fas fa-desktop"></i> View Our Tour Guide
              </Button>
            </Col>
          )}
        </Row>
        <Row>
          <Col>
            <Card>
              <Card.Body>
                <Row>
                  <Col md="4">
                    <Form.Group id="start-date-group">
                      <label>Start Date</label>
                      <CustomReactDatetime
                        inputProps={{
                          className: "form-control",
                          placeholder: "Not Selected",
                          value: selectedStartDate
                            ? moment(selectedStartDate).format(
                                "MM/DD/YYYY h:mm A"
                              )
                            : "",
                        }}
                        timeFormat={true}
                        value={selectedStartDate}
                        onChange={(value) => {
                          setSelectedStartDate(value);
                        }}
                      ></CustomReactDatetime>
                    </Form.Group>
                  </Col>
                  <Col md="4">
                    <Form.Group id="end-date-group">
                      <label>End Date</label>
                      <CustomReactDatetime
                        inputProps={{
                          className: "form-control",
                          placeholder: "Not Selected",
                          value: selectedEndDate
                            ? moment(selectedEndDate).format(
                                "MM/DD/YYYY h:mm A"
                              )
                            : "",
                        }}
                        timeFormat={true}
                        value={selectedEndDate}
                        onChange={(value) => {
                          setSelectedEndDate(value);
                        }}
                      ></CustomReactDatetime>
                    </Form.Group>
                  </Col>
                  <Col md="4">
                    <Form.Group id="message-status-filter-group">
                      <label>Message Status Filter</label>
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        name="messageStatusFilter"
                        closeMenuOnSelect={false}
                        isMulti
                        styles={customEllipsisMultiSelectStyles}
                        components={{ MultiValue: MultiValueWithCustomTooltip }}
                        value={selectedMessageStatus}
                        onChange={(messageTypes) => {
                          setSelectedMessageStatus(messageTypes);
                        }}
                        options={default_message_status_options}
                        isClearable={false}
                        placeholder="Not Selected"
                      />
                    </Form.Group>
                  </Col>
                  <Col md="4">
                    <Form.Group id="client-filter-group">
                      <label>Client Filter</label>
                      <Select
                        id="client-select"
                        className="react-select primary"
                        classNamePrefix="react-select"
                        name="clientFilter"
                        closeMenuOnSelect={false}
                        isMulti
                        styles={customEllipsisMultiSelectStyles}
                        components={{ MultiValue: MultiValueWithCustomTooltip }}
                        value={selectedClientIds}
                        onChange={(clients) => {
                          let my_clients = clients;
                          if (my_clients != null) {
                            my_clients.forEach((client_obj) => {
                              if (client_obj.value == "ALL") {
                                my_clients = clientOptions;
                              } else if (client_obj.value == "NONE") {
                                my_clients = [];
                              }
                            });
                          }
                          setSelectedClientIds(my_clients ? my_clients : []);
                          setChangedOption("client");
                        }}
                        options={[all_option, none_option, ...clientOptions]}
                        isClearable={false}
                        placeholder="None Selected"
                      />
                    </Form.Group>
                  </Col>
                  <Col md="4">
                    <Form.Group id="client-environment-filter-group">
                      <label>Client Environment Filter</label>
                      <Select
                        id="client-environment-select"
                        className="react-select primary"
                        classNamePrefix="react-select"
                        name="clientEnvironmentFilter"
                        closeMenuOnSelect={false}
                        isMulti
                        styles={customEllipsisMultiSelectStyles}
                        components={{ MultiValue: MultiValueWithCustomTooltip }}
                        value={selectedClientEnvironmentIds}
                        onChange={(clientEnvironments) => {
                          let my_client_environments = clientEnvironments;
                          if (my_client_environments != null) {
                            my_client_environments.forEach((client_env_obj) => {
                              if (client_env_obj.value == "ALL") {
                                my_client_environments =
                                  clientEnvironmentOptions;
                              } else if (client_env_obj.value == "NONE") {
                                my_client_environments = [];
                              }
                            });
                          }
                          setSelectedClientEnvironmentIds(
                            my_client_environments ? my_client_environments : []
                          );
                          setChangedOption("environment");
                        }}
                        options={[
                          all_option,
                          none_option,
                          ...clientEnvironmentOptions,
                        ]}
                        isClearable={false}
                        placeholder="None Selected"
                      />
                    </Form.Group>
                  </Col>
                  <Col md="4">
                    <Form.Group id="channel-filter-group">
                      <label>Channel Filter (Required)</label>
                      <Select
                        id="channel-select"
                        className="react-select primary"
                        classNamePrefix="react-select"
                        name="channelFilter"
                        closeMenuOnSelect={false}
                        isMulti
                        styles={customEllipsisMultiSelectStyles}
                        components={{ MultiValue: MultiValueWithCustomTooltip }}
                        value={selectedChannelIds}
                        onChange={(channels) => {
                          let my_channels = channels;
                          if (my_channels != null) {
                            my_channels.forEach((channel_obj) => {
                              if (channel_obj.value == "ALL") {
                                my_channels = channelOptions;
                              } else if (channel_obj.value == "NONE") {
                                my_channels = [];
                              }
                            });
                          }
                          setSelectedChannelIds(my_channels ? my_channels : []);
                          setChangedOption("channel");
                        }}
                        options={[all_option, none_option, ...channelOptions]}
                        isClearable={false}
                        placeholder="None Selected"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {searchByCustomMetadata && (
                  formValues.map((element, index) => (
                    <div key={`formValues-${index}`}>
                      {index > 0 ? (
                        <Row key={`and-row-${index}`}>
                          <Col md="3">
                            AND
                            <sup>
                              <CustomTooltip
                                id={`custom-metadata-tooltip`}
                                value="Custom Metadata Keys are combined using the logical 'AND'. This means to have data returned all criteria for each metadata key must be satisfied."
                              >
                                <i
                                  className="fa fa-info-circle"
                                  style={{ marginLeft: "1px" }}
                                ></i>
                              </CustomTooltip>
                            </sup>
                          </Col>
                        </Row>
                      ) : (
                        ""
                      )}
                      <Row key={`form-row-${index}`}>
                        <Col md="3">
                          <Form.Group>
                            <label>Custom Metadata Key</label>
                            <Form.Control
                              type="text"
                              className="form-control"
                              name="customMetadataKey"
                              placeholder="Enter Custom Metadata Key"
                              value={element.customMetadataKey || ""}
                              onChange={(e) => handleChange(index, e)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md="3">
                          <Form noValidate validated={validated}>
                            <Form.Group>
                              <label>Custom Metadata Value List</label>
                              <sup>
                                <CustomTooltip
                                  id={`custom-metadata-value-tooltip`}
                                  value="Please separate multiple values for this metadata key to be found with a comma. Any value in the list found will satisfy the criteria of this report."
                                >
                                  <i
                                    className="fa fa-info-circle"
                                    style={{ marginLeft: "1px" }}
                                  ></i>
                                </CustomTooltip>
                              </sup>
                              <Form.Control
                                required
                                id={`validationCustomMetadataValueListId-${index}`}
                                placeholder='value1, "value2,with_comma", value3'
                                type="text"
                                name="customMetadataValueList"
                                value={element.customMetadataValueList || ""}
                                minLength="1"
                                maxLength="500"
                                pattern='^(?:(?:[^"]*"[^"]*")*[^"]*)*$'
                                onChange={(e) => {
                                  setValidated(true);
                                  handleChange(index, e);
                                }}
                              ></Form.Control>
                              <Form.Control.Feedback type="invalid">
                                Minimum allowed character length is 1 and
                                maximum allowed length is 500.
                              </Form.Control.Feedback>
                              <Form.Control.Feedback type="invalid">
                                There must be an even number of double
                                quotes.
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Form>
                        </Col>
                        <Col md="6">
                          <div style={{ paddingTop: "26px" }}>
                            <Button
                              variant="primary"
                              className="btn-outline"
                              onClick={() => addFormFields()}
                            >
                              Add Criteria
                            </Button>
                            <Button
                              variant="danger"
                              className="btn-outline"
                              style={{ marginLeft: "30px" }}
                              onClick={() => removeFormFields(index)}
                            >
                              Remove
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  ))
                )}
                <Row>
                  <Col>
                    {!searchByCustomMetadata && (
                      <Button
                        id="search-by-custom-metadata-button"
                        variant="primary"
                        className="btn-outline"
                        onClick={() => {
                          setSearchByCustomMetadata(true);
                        }}
                      >
                        Search Custom Metadata
                      </Button>
                    )}
                    <Button
                      id="schedule-report-button"
                      variant="primary"
                      style={{
                        marginLeft: !searchByCustomMetadata ? "17px" : "0px",
                      }}
                      onClick={() => {
                        if (selectedStartDate && selectedEndDate && selectedStartDate.isAfter(selectedEndDate)) {
                          notify(
                            "danger",
                            "Start date must be lesser than end date"
                          );
                        } else if (selectedChannelIds.length == 0) {
                          notify(
                            "danger",
                            "Please select at least one channel filter"
                          );
                        } else if (searchByCustomMetadata && !isCustomMetadataValid()) {
                          notify(
                            "danger",
                            "All custom metadata keys have not been selected"
                          );
                        } else if (searchByCustomMetadata && !isCustomMetadataValueValid()) {
                          notify(
                            "danger",
                            "All custom metadata values have not been entered or contain invalid quotes"
                          );
                        } else {
                          setSelectedRunReportDate(moment());
                          setShowScheduleReportModal(true);
                        }
                      }}
                    >
                      Schedule Report
                    </Button>
                    <Button
                      id="reset-filters-button"
                      variant="default"
                      className="btn-outline"
                      style={{ marginLeft: "30px" }}
                      onClick={() => {
                        setSelectedStartDate(moment().startOf("day"));
                        setSelectedEndDate(moment().endOf("day"));
                        setSelectedMessageStatus([]);
                        setSelectedClientIds([]);
                        setSelectedClientEnvironmentIds([]);
                        setSelectedChannelIds([]);
                        setClientOptions(default_client_options);
                        setClientEnvironmentOptions(
                          default_environment_options
                        );
                        setChannelOptions(default_channel_options);
                        setChangedOption(null);
                        setFormValues([
                          { customMetadataKey: "", customMetadataValueList: "" },
                        ]);
                        setSearchByCustomMetadata(false);
                      }}
                    >
                      Reset
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Modal
          toggle={() => setShowScheduleReportModal(!showScheduleReportModal)}
          size="md"
          isOpen={showScheduleReportModal}
          scrollable={false}
        >
          <ModalBody>
            <Row>
              <Col>
                <h5>Schedule Report</h5>
              </Col>
              {currentUser.groupName === "external" && (
                <Col className="my-auto text-right">
                  <Button
                    variant="secondary"
                    className="btn-outline"
                    onClick={() => {
                      setSteps(scheduleReportModalSteps);
                      setCurrentStep(0);
                      setIsOpen(true);
                    }}
                  >
                    <i className="fas fa-desktop"></i> View Our Tour Guide
                  </Button>
                </Col>
              )}
            </Row>
            <Row>
              <Col>
                <Form.Group id="scheduleReportNameFormGroup">
                  <label>Report Name</label>
                  <Form.Control
                    id="reportName"
                    placeholder="Report Name"
                    disabled={false}
                    name="reportName"
                    value={selectedReportName}
                    onChange={(e) => {
                      setSelectedReportName(e.target.value);
                    }}
                  ></Form.Control>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group id="scheduleReportExecutionTimeFormGroup">
                  <label>Report Execution Time</label>
                  <ReactDatetime
                    inputProps={{
                      style: {cursor: "pointer"},
                    }}
                    value={selectedRunReportDate}
                    onChange={(value) => {
                      setSelectedRunReportDate(value);
                    }}
                  ></ReactDatetime>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group id="scheduleDistributeReportToOrganizationFormGroup">
                  <Form.Check>
                    <Form.Check.Label>
                      <Form.Check.Input
                        type="checkbox"
                        checked={selectedReportDistribution}
                        onChange={(e) => {
                          setSelectedReportDistribution(
                            !selectedReportDistribution
                          );
                        }}
                      ></Form.Check.Input>
                      <span className="form-check-sign"></span>
                      Distribute report to Organization
                    </Form.Check.Label>
                  </Form.Check>
                </Form.Group>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              id="scheduleCancelButton"
              variant="secondary"
              onClick={() => {
                setShowScheduleReportModal(false);
                setSelectedReportName("My Manifest Outcome and Content Report");
                setSelectedReportDistribution(false);
              }}
            >
              Cancel
            </Button>
            <Button
              id="scheduleConfirmButton"
              variant="primary"
              onClick={() => {
                if (selectedReportName == null || selectedReportName == "") {
                  notify("danger", "The report name cannot be blank");
                } else {
                  addReport(generateReportParameters());
                  setShowScheduleReportModal(false);
                }
              }}
            >
              Confirm
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </>
  );
}

export default ManifestOutcomeContentReport;
